import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Tables } from '@tellowup/db';

// Narrow types for specific Supabase query result shapes
type ProfileAdminSummary = Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email' | 'role' | 'is_active'>;
type BranchUserSelect = Pick<Tables<'branch_user'>, 'user_id' | 'branch_id'>;
type BranchSelect = Pick<Tables<'branches'>, 'id' | 'name'>;

export interface UserFilters {
  organizationId: string | null;
  search?: string | null;
  page?: number;
}

export interface UserWithBranches {
  id: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  branches: string[];
}

interface UseAdminUsersListResult {
  users: UserWithBranches[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  toggleStatus: (userId: string, current: boolean) => Promise<void>;
}

const PAGE_SIZE = 20;

export function useAdminUsersList(filters: UserFilters): UseAdminUsersListResult {
  const { organizationId, search = null, page = 0 } = filters;

  const [users, setUsers] = useState<UserWithBranches[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setUsers([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchUsers() {
      setIsLoading(true);
      setError(null);

      try {
        const start = page * PAGE_SIZE;
        const end = start + PAGE_SIZE - 1;

        // Build profiles query
        let query = supabase
          .from('profiles')
          .select('id, full_name, email, role, is_active', { count: 'exact' })
          .eq('organization_id', organizationId);

        // Apply search filter
        if (search) {
          query = query.or(
            `full_name.ilike.%${search}%,email.ilike.%${search}%`
          );
        }

        // Apply pagination
        const { data: profilesData, error: profilesError, count } = await query.range(start, end);

        if (profilesError) {
          if (!cancelled) {
            setError(profilesError.message);
            setUsers([]);
            setTotalCount(0);
          }
          return;
        }

        // Fetch branch_user and branches for this org (client-side join)
        const userIds = (profilesData ?? []).map((p: ProfileAdminSummary) => p.id);

        let branchMap: Map<string, string[]> = new Map();

        if (userIds.length > 0) {
          const { data: branchUsers } = await supabase
            .from('branch_user')
            .select('user_id, branch_id')
            .in('user_id', userIds);

          const branchIds = [...new Set((branchUsers ?? []).map((bu: BranchUserSelect) => bu.branch_id))];

          let branches: BranchSelect[] = [];
          if (branchIds.length > 0) {
            const { data: branchesData } = await supabase
              .from('branches')
              .select('id, name')
              .in('id', branchIds);
            branches = (branchesData as BranchSelect[]) ?? [];
          }

          // Build userId -> branchNames map
          const branchNameMap = new Map(branches.map((b: BranchSelect) => [b.id, b.name]));
          branchMap = new Map();
          for (const bu of (branchUsers ?? [])) {
            if (!branchMap.has(bu.user_id)) {
              branchMap.set(bu.user_id, []);
            }
            const name = branchNameMap.get(bu.branch_id);
            if (name) {
              branchMap.get(bu.user_id)!.push(name);
            }
          }
        }

        if (!cancelled) {
          const mapped: UserWithBranches[] = (profilesData ?? []).map((p: ProfileAdminSummary) => ({
            id: p.id,
            full_name: p.full_name || '—',
            email: p.email || '—',
            role: p.role || 'technician',
            is_active: p.is_active ?? true,
            branches: branchMap.get(p.id) ?? [],
          }));
          setUsers(mapped);
          setTotalCount(count || 0);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch users');
          setUsers([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchUsers();

    return () => {
      cancelled = true;
    };
  }, [organizationId, search, page, refreshTrigger]);

  const toggleStatus = useCallback(
    async (userId: string, current: boolean) => {
      const targetActive = !current;

      // Optimistic update
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, is_active: targetActive } : u))
      );

      try {
        const { error: rpcError } = await supabase.rpc('admin_deactivate_user', { p_user_id: userId });
        if (rpcError) throw rpcError;
      } catch (err) {
        // Rollback
        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, is_active: current } : u))
        );
        setError('Failed to update user status');
      }
    },
    []
  );

  return {
    users,
    totalCount,
    isLoading,
    error,
    refetch,
    toggleStatus,
  };
}